import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Calendar, Clock, Users, Building, CheckCircle2 } from 'lucide-react';
import { toast } from './ui/sonner';

export const DemoRequestModal = ({ children, isOpen: externalIsOpen, setIsOpen: externalSetIsOpen, preselectedIndustry }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Verwende externe Kontrolle wenn verfügbar, sonst interne
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalSetIsOpen || setInternalIsOpen;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: preselectedIndustry || '',
    employees: '',
    locations: '',
    currentSolution: '',
    specificNeeds: '',
    preferredDate: '',
    preferredTime: '',
    demoType: 'online',
    acceptTerms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Aktualisiere die Branche wenn eine neue vorausgewählt wird
  useEffect(() => {
    if (preselectedIndustry && preselectedIndustry !== formData.industry) {
      setFormData(prev => ({ ...prev, industry: preselectedIndustry }));
    }
  }, [preselectedIndustry]);

  const industries = [
    'Spielhalle',
    'Wettbüro',
    'Gastronomie mit Geldspielgeräten',
    'Veranstaltungen',
    'Casino',
    'Sonstige'
  ];

  const timeSlots = [
    '09:00 - 10:00',
    '10:00 - 11:00',
    '11:00 - 12:00',
    '13:00 - 14:00',
    '14:00 - 15:00',
    '15:00 - 16:00',
    '16:00 - 17:00'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8010';

      const response = await fetch(`${backendUrl}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'demo_request',
          form_data: formData,
          timestamp: new Date().toISOString()
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Demo-Anfrage erfolgreich gesendet!', {
          description: 'Wir melden uns innerhalb von 24 Stunden bei Ihnen.',
          duration: 5000,
          icon: <CheckCircle2 className="w-5 h-5" />
        });
        setIsOpen(false);
        setFormData({
          firstName: '', lastName: '', email: '', phone: '', company: '',
          industry: '', employees: '', locations: '', currentSolution: '',
          specificNeeds: '', preferredDate: '', preferredTime: '', demoType: 'online'
        });
      } else if (result.blocked) {
        // Geo-blocked submission
        toast.error('Online-Anfrage nicht verfügbar', {
          description: result.contact_message || 'Bitte kontaktieren Sie uns telefonisch oder per E-Mail.',
          duration: 8000
        });
      } else {
        toast.error('Fehler beim Senden', {
          description: result.message || 'Bitte versuchen Sie es erneut.',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Verbindungsfehler', {
        description: 'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
        duration: 4000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-teal-500" />
            Live Demo anfragen
          </DialogTitle>
          <DialogDescription>
            Erfahren Sie in einer persönlichen Demo, wie IDA KI Ihre Compliance-Prozesse revolutionieren kann.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          {/* Persönliche Daten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">Vorname *</Label>
              <Input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Nachname *</Label>
              <Input
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">E-Mail-Adresse *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="phone">Telefonnummer *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="mt-1"
              />
            </div>
          </div>

          {/* Unternehmensdaten */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-teal-500" />
              Unternehmensdaten
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company">Unternehmensname *</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="industry">Branche *</Label>
                <Select value={formData.industry} onValueChange={(value) => handleSelectChange('industry', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Wählen Sie Ihre Branche" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="employees">Anzahl Mitarbeiter</Label>
                <Select value={formData.employees} onValueChange={(value) => handleSelectChange('employees', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Mitarbeiteranzahl" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-5">1-5 Mitarbeiter</SelectItem>
                    <SelectItem value="6-20">6-20 Mitarbeiter</SelectItem>
                    <SelectItem value="21-50">21-50 Mitarbeiter</SelectItem>
                    <SelectItem value="51-100">51-100 Mitarbeiter</SelectItem>
                    <SelectItem value="100+">Über 100 Mitarbeiter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="locations">Anzahl Standorte</Label>
                <Input
                  id="locations"
                  name="locations"
                  type="number"
                  min="1"
                  value={formData.locations}
                  onChange={handleInputChange}
                  className="mt-1"
                  placeholder="z.B. 3"
                />
              </div>
            </div>
          </div>

          {/* Demo-Präferenzen */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-teal-500" />
              Demo-Präferenzen
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="demoType">Demo-Art</Label>
                <Select value={formData.demoType} onValueChange={(value) => handleSelectChange('demoType', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online Demo</SelectItem>
                    <SelectItem value="vorort">Vor-Ort Demo</SelectItem>
                    <SelectItem value="telefonisch">Telefonische Demo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="preferredDate">Wunschtermin</Label>
                <Input
                  id="preferredDate"
                  name="preferredDate"
                  type="date"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label htmlFor="preferredTime">Wunschzeit</Label>
                <Select value={formData.preferredTime} onValueChange={(value) => handleSelectChange('preferredTime', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Zeitslot wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Zusätzliche Informationen */}
          <div className="border-t pt-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="currentSolution">Aktuelle Lösung (optional)</Label>
                <Input
                  id="currentSolution"
                  name="currentSolution"
                  value={formData.currentSolution}
                  onChange={handleInputChange}
                  className="mt-1"
                  placeholder="Welche Software verwenden Sie aktuell?"
                />
              </div>
              <div>
                <Label htmlFor="specificNeeds">Spezielle Anforderungen</Label>
                <Textarea
                  id="specificNeeds"
                  name="specificNeeds"
                  value={formData.specificNeeds}
                  onChange={handleInputChange}
                  className="mt-1"
                  placeholder="Gibt es spezielle Funktionen oder Bereiche, die Sie besonders interessieren?"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Rechtliches */}
          <div className="border-t pt-6">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="acceptTermsDemo"
                checked={formData.acceptTerms}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, acceptTerms: checked }))}
                className="mt-1"
              />
              <Label htmlFor="acceptTermsDemo" className="text-sm leading-relaxed">
                Ich akzeptiere die{' '}
                <a href="/agb" target="_blank" className="text-teal-600 hover:text-teal-700 font-medium">AGB</a>
                {' '}und stimme der Kontaktaufnahme sowie Verarbeitung meiner Daten zum Zweck der Demo-Organisation zu.
              </Label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="border-t pt-6 flex justify-end space-x-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button 
              type="submit" 
              className="bg-teal-500 hover:bg-teal-600"
              disabled={isSubmitting || !formData.acceptTerms}
            >
              {isSubmitting ? 'Sende Anfrage...' : 'Demo anfragen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};